// ============================================================================
// Hooks for the Phase 7 close/hold case-level actions.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  caseClosureService,
  caseHoldService,
} from 'api/services/caseClosureService';
import type {
  CaseClosure,
  ClosureReasonOption,
  CloseCaseRequest,
  ReopenCaseRequest,
} from 'api/types/caseClosure.types';
import type {
  CaseHold,
  CaseHoldHistoryEntry,
  HoldCaseRequest,
  HoldReasonOption,
  ResumeCaseRequest,
} from 'api/types/caseHold.types';

// --- Active closure (or null) for a case ------------------------------------
export const useActiveCaseClosure = (caseId: string | null | undefined) => {
  const [data, setData] = useState<CaseClosure | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseId) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      setData(await caseClosureService.getActive(caseId));
    } catch (e) {
      setError(e as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

// --- Closure reasons available for a case (filtered server-side) ------------
export const useClosureReasonOptions = (caseId: string | null | undefined) => {
  const [data, setData] = useState<ClosureReasonOption[]>([]);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true);
    try { setData(await caseClosureService.getReasonOptions(caseId)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

// --- Mutations: close / reopen ----------------------------------------------
export const useCloseCase = () => {
  const [loading, setLoading] = useState(false);
  const close = useCallback(async (caseId: string, body: CloseCaseRequest) => {
    setLoading(true);
    try { return await caseClosureService.close(caseId, body); }
    finally { setLoading(false); }
  }, []);
  return { close, loading };
};

export const useReopenCase = () => {
  const [loading, setLoading] = useState(false);
  const reopen = useCallback(async (caseId: string, body: ReopenCaseRequest) => {
    setLoading(true);
    try { return await caseClosureService.reopen(caseId, body); }
    finally { setLoading(false); }
  }, []);
  return { reopen, loading };
};

// --- Current hold + reasons + mutations -------------------------------------
export const useCurrentCaseHold = (caseId: string | null | undefined) => {
  const [data, setData] = useState<CaseHold | null>(null);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async () => {
    if (!caseId) { setData(null); return; }
    setLoading(true);
    try { setData(await caseHoldService.getCurrent(caseId)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

export const useCaseHoldHistory = (caseId: string | null | undefined) => {
  const [data, setData] = useState<CaseHoldHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true);
    try { setData(await caseHoldService.getHistory(caseId)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

export const useHoldReasonOptions = (caseId: string | null | undefined) => {
  const [data, setData] = useState<HoldReasonOption[]>([]);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true);
    try { setData(await caseHoldService.getReasonOptions(caseId)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

export const useHoldCase = () => {
  const [loading, setLoading] = useState(false);
  const hold = useCallback(async (caseId: string, body: HoldCaseRequest) => {
    setLoading(true);
    try { return await caseHoldService.hold(caseId, body); }
    finally { setLoading(false); }
  }, []);
  return { hold, loading };
};

export const useResumeCase = () => {
  const [loading, setLoading] = useState(false);
  const resume = useCallback(async (caseId: string, body: ResumeCaseRequest) => {
    setLoading(true);
    try { return await caseHoldService.resume(caseId, body); }
    finally { setLoading(false); }
  }, []);
  return { resume, loading };
};
